import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  ClipboardList,
  FileText,
  Github,
  LogIn,
  LogOut,
  type LucideIcon,
  MessageSquare,
  Search,
  Settings,
  Unlock,
  XCircle,
} from 'lucide-react';
import { activityApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { formatDateTime } from '@/lib/utils';

const ACTION_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  'submission.created': { label: 'Заявка создана', icon: FileText },
  'submission.approved': { label: 'Заявка одобрена', icon: CheckCircle },
  'submission.rejected': { label: 'Заявка отклонена', icon: XCircle },
  'ban.created': { label: 'Бан создан', icon: Ban },
  'ban.lifted': { label: 'Бан снят', icon: Unlock },
  'appeal.created': { label: 'Апелляция создана', icon: MessageSquare },
  'appeal.approved': { label: 'Апелляция одобрена', icon: CheckCircle },
  'appeal.rejected': { label: 'Апелляция отклонена', icon: XCircle },
  'auth.login': { label: 'Вход в систему', icon: LogIn },
  'auth.logout': { label: 'Выход из системы', icon: LogOut },
  'settings.updated': { label: 'Настройки обновлены', icon: Settings },
  'github.issue_created': { label: 'Issue создан', icon: Github },
  'ai.toxicity_detected': { label: 'Токсичность обнаружена', icon: AlertTriangle },
  'ai.duplicate_detected': { label: 'Дубликат обнаружен', icon: Search },
};

const ACTOR_LABELS: Record<string, { label: string; color: string }> = {
  user: { label: 'Пользователь', color: 'text-gray-400' },
  admin: { label: 'Админ', color: 'text-neon-magenta' },
  system: { label: 'Система', color: 'text-neon-cyan' },
  ai: { label: 'AI', color: 'text-neon-green' },
};

export function ActivityPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['activity'],
    queryFn: () => activityApi.list({ limit: 50 }),
  });

  return (
    <Layout title="Активность" subtitle="Лог действий">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="cyber-card overflow-hidden">
          <div className="divide-y divide-cyber-light">
            {data?.logs.map((log) => {
              const action = ACTION_LABELS[log.action] ?? {
                label: log.action,
                icon: ClipboardList,
              };
              const actor = ACTOR_LABELS[log.actor_type] ?? {
                label: log.actor_type,
                color: 'text-gray-400',
              };
              const Icon = action.icon;

              return (
                <div key={log.id} className="p-3 hover:bg-cyber-gray/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-cyber-gray flex items-center justify-center text-gray-400">
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm text-gray-200">{action.label}</span>
                        <span className={`text-xs ${actor.color}`}>• {actor.label}</span>
                      </div>
                      {log.target_type && (
                        <p className="text-xs text-gray-500">
                          {log.target_type} #{log.target_id}
                        </p>
                      )}
                      {log.details && (
                        <pre className="text-xs text-gray-600 mt-1 overflow-hidden text-ellipsis whitespace-pre-wrap">
                          {log.details}
                        </pre>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
            {(!data?.logs || data.logs.length === 0) && (
              <div className="p-8 text-center text-gray-500">Нет записей активности</div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
